import find from 'lodash/find';
import { allProfileTypes } from '../../constants/profileTypes';

const MODE_PRIORITY = [
  allProfileTypes.ADMIN,
  allProfileTypes.SUPERVISOR,
  allProfileTypes.RESIDENT
];

const RESIDENT_PROFILE_TYPES = [
  allProfileTypes.OWNER,
  allProfileTypes.TENANT,
  allProfileTypes.CORESIDENT
];

function hasValidProfile(profileType) {
  return [
    allProfileTypes.ADMIN,
    allProfileTypes.SUPERVISOR,
    allProfileTypes.GUARD,
    allProfileTypes.OWNER,
    allProfileTypes.TENANT,
    allProfileTypes.CORESIDENT
  ].includes(profileType);
}

function isEnabledProfile(profile) {
  return profile.status === 'ENABLED' && hasValidProfile(profile.type);
}

function isAdminProfile(profile) {
  return (
    profile.status === 'ENABLED' &&
    (profile.admin === true || profile.type === allProfileTypes.ADMIN)
  );
}

function isSupervisorProfile(profile) {
  return (
    profile.status === 'ENABLED' && profile.type === allProfileTypes.SUPERVISOR
  );
}

function isResidentProfile(profile) {
  return (
    profile.status === 'ENABLED' &&
    RESIDENT_PROFILE_TYPES.includes(profile.type)
  );
}

function profileMatchesMode(profile, sessionType) {
  if (sessionType === allProfileTypes.ADMIN) {
    return isAdminProfile(profile);
  }
  if (sessionType === allProfileTypes.SUPERVISOR) {
    return isSupervisorProfile(profile);
  }
  if (sessionType === allProfileTypes.RESIDENT) {
    return isResidentProfile(profile);
  }
  return false;
}

function getModeForProfile(profile) {
  if (isAdminProfile(profile)) {
    return allProfileTypes.ADMIN;
  }
  if (isSupervisorProfile(profile)) {
    return allProfileTypes.SUPERVISOR;
  }
  if (isResidentProfile(profile)) {
    return allProfileTypes.RESIDENT;
  }
  return null;
}

function getEnabledValidProfiles(userSession) {
  return (userSession?.profiles || []).filter(
    profile =>
      isAdminProfile(profile) ||
      isSupervisorProfile(profile) ||
      isResidentProfile(profile)
  );
}

function getModesForProfiles(profiles) {
  const modes = [];
  MODE_PRIORITY.forEach(mode => {
    if (profiles.some(profile => profileMatchesMode(profile, mode))) {
      modes.push(mode);
    }
  });
  return modes;
}

function getModesForCommunity(userSession, communityId) {
  const profiles = getEnabledValidProfiles(userSession).filter(
    profile => profile.community?.id === communityId
  );
  return getModesForProfiles(profiles);
}

function resolveSessionType(availableModes, preferredType) {
  if (preferredType && availableModes.includes(preferredType)) {
    return preferredType;
  }
  return availableModes[0] || allProfileTypes.RESIDENT;
}

function getInitialSessionType(userSession) {
  const modes = getModesForProfiles(getEnabledValidProfiles(userSession));
  return resolveSessionType(modes, null);
}

function getSessionProfiles(userSession, sessionType) {
  return getEnabledValidProfiles(userSession).filter(profile =>
    profileMatchesMode(profile, sessionType)
  );
}

function getDefaultProfile(userSession, profiles) {
  return find(profiles, profile => profile.id == userSession.defaultProfile);
}

function getProfileForMode(userSession, sessionType, communityId) {
  const modeProfiles = getSessionProfiles(userSession, sessionType);
  const communityProfiles = communityId
    ? modeProfiles.filter(profile => profile.community?.id === communityId)
    : modeProfiles;

  if (sessionType === allProfileTypes.RESIDENT) {
    return (
      getDefaultProfile(userSession, communityProfiles) ||
      communityProfiles[0] ||
      getDefaultProfile(userSession, modeProfiles) ||
      modeProfiles[0] ||
      null
    );
  }

  return communityProfiles[0] || modeProfiles[0] || null;
}

function getReachableCommunities(userSession) {
  const communities = getEnabledValidProfiles(userSession)
    .map(profile => profile.community)
    .filter(Boolean);

  return communities.filter(
    (community, index, allCommunities) =>
      allCommunities.findIndex(c => c.id === community.id) === index
  );
}

function resolveCommunitySwitch(userSession, selectedCommunity, currentType) {
  const communityId = selectedCommunity.id;
  const availableModes = getModesForCommunity(userSession, communityId);
  const sessionType = resolveSessionType(availableModes, currentType);
  const profile =
    getProfileForMode(userSession, sessionType, communityId) ||
    find(
      getEnabledValidProfiles(userSession),
      p => p.community?.id === communityId
    );
  const profiles = getSessionProfiles(userSession, sessionType);

  return { sessionType, profile, profiles };
}

function resolveModeSwitch(userSession, sessionType, currentCommunityId) {
  const availableModes = currentCommunityId
    ? getModesForCommunity(userSession, currentCommunityId)
    : getModesForProfiles(getEnabledValidProfiles(userSession));
  const resolvedType = resolveSessionType(availableModes, sessionType);
  const profile = getProfileForMode(
    userSession,
    resolvedType,
    currentCommunityId
  );
  const profiles = getSessionProfiles(userSession, resolvedType);

  return { sessionType: resolvedType, profile, profiles };
}

export {
  MODE_PRIORITY,
  hasValidProfile,
  isEnabledProfile,
  isAdminProfile,
  isSupervisorProfile,
  isResidentProfile,
  profileMatchesMode,
  getModeForProfile,
  getEnabledValidProfiles,
  getModesForProfiles,
  getModesForCommunity,
  resolveSessionType,
  getInitialSessionType,
  getSessionProfiles,
  getProfileForMode,
  getReachableCommunities,
  resolveCommunitySwitch,
  resolveModeSwitch
};
